import { useEffect, useRef, useState } from 'react';
import posthog from 'posthog-js';
import SuperTokens from 'supertokens-auth-react';
import Session from 'supertokens-auth-react/recipe/session';
import EmailPassword from 'supertokens-auth-react/recipe/emailpassword';
import EmailVerification from 'supertokens-auth-react/recipe/emailverification';
import {
  signIn as signInSupertokens,
  signUp as signUpSupertokens,
  sendPasswordResetEmail as sendPasswordResetEmailSupertokens,
  submitNewPassword as submitNewPasswordSupertokens,
} from 'supertokens-web-js/recipe/emailpassword';
import {
  attemptRefreshingSession,
  signOut as signOutSupertokens,
  useSessionContext,
} from 'supertokens-auth-react/recipe/session';
import { clearGCache, getConfig, getConfigOrThrow, login, logout, NotifyInfo, store } from 'gossamer-front-end';
import { LoadedSessionContext } from 'supertokens-auth-react/lib/build/recipe/session/types';
import { SecurityContext } from 'gossamer-unified';

export type User = {
  id: string;
  email: string;
};

export type Message = {
  type: 'error' | 'info';
  text: string;
};

type SuperTokensGeneralError = {
  isSuperTokensGeneralError: true;
  message: string;
};

let initialised = false;

const isSuperTokensGeneralError = (error: unknown): error is SuperTokensGeneralError => {
  return (
    typeof error === 'object' && error !== null && (error as SuperTokensGeneralError).isSuperTokensGeneralError === true
  );
};

const captureSessionRecovery = (properties: Record<string, unknown>) => {
  try {
    posthog.capture('session_recovery', properties);
  } catch (error) {
    console.warn('Failed to capture session recovery telemetry', error);
  }

  try {
    window.dispatchEvent(new CustomEvent('careerbook:session-recovery', { detail: properties }));
  } catch {
    // Ignore environments where CustomEvent is unavailable.
  }
};

export const initialiseSupertokensAuth = () => {
  if (initialised) {
    return;
  }
  initialised = true;
  SuperTokens.init({
    appInfo: {
      appName: 'Careerbook',
      apiDomain: getConfigOrThrow('gossamer-be-origin'),
      websiteDomain: getConfigOrThrow('gossamer-fe-origin'),
      apiBasePath: '/auth',
      websiteBasePath: '/auth',
    },
    recipeList: [
      Session.init(),
      EmailPassword.init(),
      EmailVerification.init({
        mode: 'OPTIONAL',
        disableDefaultUI: true,
      }),
    ],
  });
};

export function useAuth() {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<Message | undefined>(undefined);
  const [isRecoveringSession, setIsRecoveringSession] = useState(false);
  const session = useSessionContext();
  const superTokensUserId = (session as LoadedSessionContext)?.userId || undefined;
  const recoveryAttemptedRef = useRef(false);
  initialiseSupertokensAuth();

  useEffect(() => {
    if (getConfig('login-bypass') === 'true') {
      return;
    }
    if (session.loading) {
      return; // wait for SuperTokens session to resolve
    }
    const isAuthPage = ['login', 'logout'].some((segment) => window.location.pathname.indexOf(segment) !== -1);
    const doesSessionExist = 'doesSessionExist' in session ? session.doesSessionExist : !!superTokensUserId;
    const securityContext = (session as LoadedSessionContext)?.accessTokenPayload?.securityContext as
      | SecurityContext
      | undefined;

    if (!!superTokensUserId && !!securityContext?.roleId && securityContext?.roleId !== 'none') {
      recoveryAttemptedRef.current = false;
      store.dispatch(login(securityContext));
      return;
    }

    if (!doesSessionExist) {
      recoveryAttemptedRef.current = false;
      store.dispatch(logout(isAuthPage ? {} : { returnToOnceLoggedIn: window.location.href }));
      return;
    }

    if (isRecoveringSession || recoveryAttemptedRef.current || isAuthPage) {
      return;
    }

    let cancelled = false;
    const recoverSession = async () => {
      recoveryAttemptedRef.current = true;
      setIsRecoveringSession(true);

      try {
        const tryRefresh = async () => {
          try {
            return await attemptRefreshingSession();
          } catch (error) {
            console.warn('Gossamer session refresh attempt failed', error);
            return false;
          }
        };

        let refreshed = await tryRefresh();
        if (refreshed) {
          captureSessionRecovery({
            surface: 'gossamer',
            outcome: 'success',
            attempt: 1,
            path: window.location.pathname,
          });
        }
        if (!refreshed) {
          await new Promise((resolve) => setTimeout(resolve, 500));
          refreshed = await tryRefresh();
          if (refreshed) {
            captureSessionRecovery({
              surface: 'gossamer',
              outcome: 'success',
              attempt: 2,
              path: window.location.pathname,
            });
          }
        }

        if (cancelled || refreshed) {
          return;
        }

        captureSessionRecovery({
          surface: 'gossamer',
          outcome: 'failed',
          attempt: 2,
          path: window.location.pathname,
        });

        const payload: any = {
          returnToOnceLoggedIn: window.location.href,
        };
        store.dispatch(logout(payload));
      } finally {
        if (!cancelled) {
          setIsRecoveringSession(false);
        }
      }
    };

    void recoverSession();

    return () => {
      cancelled = true;
    };
  }, [superTokensUserId, session.loading, session, isRecoveringSession]);

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    try {
      let response = await signUpSupertokens({
        formFields: [
          {
            id: 'email',
            value: email,
          },
          {
            id: 'password',
            value: password,
          },
          {
            id: 'firstName',
            value: firstName,
          },
          {
            id: 'lastName',
            value: lastName,
          },
        ],
      });

      if (response.status === 'FIELD_ERROR') {
        // one of the input formFields failed validation
        response.formFields.forEach((formField) => {
          if (formField.id === 'email') {
            // Email validation failed (for example incorrect email syntax),
            // or the email is not unique.
          } else if (formField.id === 'password') {
            // Password validation failed.
            // Maybe it didn't match the password strength
          }

          setMessage({ type: 'error', text: formField.error });
        });

        // Return false for the caller to know that submission failed
        return false;
      } else if (response.status === 'SIGN_UP_NOT_ALLOWED') {
        // the reason string is a user friendly message
        // about what went wrong. It can also contain a support code which users
        // can tell you so you know why their sign up was not allowed.
        setMessage({ type: 'error', text: response.reason });
        return false;
      } else {
        // sign up successful. The session tokens are automatically handled by
        // the frontend SDK.
        return response.user;
      }
    } catch (err) {
      if (isSuperTokensGeneralError(err)) {
        // this may be a custom error message sent from the API by you.
        setMessage({ type: 'error', text: err.message });
      } else {
        console.log(err);
        setMessage({ type: 'error', text: 'Oops! Something went wrong.' });
      }

      return false;
    }
  };

  const signIn = async (email: string, password: string): Promise<NotifyInfo> => {
    let notify: NotifyInfo = ['unknown', 'danger'];
    try {
      const response = await signInSupertokens({
        formFields: [
          {
            id: 'email',
            value: email,
          },
          {
            id: 'password',
            value: password,
          },
        ],
      });

      if (response.status === 'FIELD_ERROR') {
        response.formFields.forEach((formField) => {
          if (formField.id === 'email') {
            // Email validation failed (for example incorrect email syntax).
            notify = [formField.error, 'danger'];
          }
        });
        return notify;
      } else if (response.status === 'WRONG_CREDENTIALS_ERROR') {
        return ['Email password combination is incorrect', 'danger'];
      } else if (response.status === 'SIGN_IN_NOT_ALLOWED') {
        // the reason string is a user friendly message
        // about what went wrong. It can also contain a support code which users
        // can tell you so you know why their sign in was not allowed.
        return [response.reason, 'danger'];
      } else {
        // sign in successful. The session tokens are automatically handled by the frontend SDK.
        // getIdentity();
        return ['log-in successful', 'success'];
      }
    } catch (err) {
      if (isSuperTokensGeneralError(err)) {
        // this may be a custom error message sent from the API by you.
        return [err.message, 'danger'];
      } else {
        return ['Something is not right, check your credentials', 'danger'];
      }
    }
  };

  const signOut = async () => {
    try {
      setIsLoading(true);
      await signOutSupertokens();
      await store.dispatch(logout({}));
      await store.dispatch(clearGCache({}));
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  async function sendPasswordResetEmail(email: string) {
    try {
      let response = await sendPasswordResetEmailSupertokens({
        formFields: [
          {
            id: 'email',
            value: email,
          },
        ],
      });

      if (response.status === 'FIELD_ERROR') {
        const errors: Record<string, string> = {};

        response.formFields.forEach((formField) => {
          if (formField.id === 'email') {
            setMessage({ type: 'error', text: formField.error });
          }
        });
        return false;
      } else if (response.status === 'PASSWORD_RESET_NOT_ALLOWED') {
        // this can happen due to automatic account linking. Please read our account linking docs
        setMessage({ type: 'error', text: 'Password resets are not allowed for this account.' });
        return false;
      } else {
        // reset password email sent.
        setMessage({ type: 'info', text: 'Please check your email inbox for the password reset link.' });
        return true;
      }
    } catch (err) {
      if (isSuperTokensGeneralError(err)) {
        // this may be a custom error message sent from the API by you.
        setMessage({ type: 'error', text: err.message });
      } else {
        console.error(err);
        setMessage({ type: 'error', text: 'An unexpected error occured.' });
      }

      return false;
    }
  }

  async function submitNewPassword(newPassword: string) {
    try {
      let response = await submitNewPasswordSupertokens({
        formFields: [
          {
            id: 'password',
            value: newPassword,
          },
        ],
      });

      if (response.status === 'FIELD_ERROR') {
        response.formFields.forEach((formField) => {
          if (formField.id === 'password') {
            setMessage({ type: 'error', text: formField.error });
          }
        });
        return false;
      } else if (response.status === 'RESET_PASSWORD_INVALID_TOKEN_ERROR') {
        // the password reset token in the URL is invalid, expired, or already consumed
        setMessage({ type: 'error', text: 'Password reset failed. Please request a new password reset link.' });
        return false;
      } else {
        setMessage({ type: 'info', text: 'Password reset successful! Redirecting you to the login page.' });
        return true;
      }
    } catch (err) {
      if (isSuperTokensGeneralError(err)) {
        // this may be a custom error message sent from the API by you.
        setMessage({ type: 'error', text: err.message });
      } else {
        setMessage({ type: 'error', text: 'Oops! Something went wrong.' });
      }

      return false;
    }
  }

  return {
    isLoading,
    message,
    signUp,
    signIn,
    signOut,
    sendPasswordResetEmail,
    submitNewPassword,
  };
}
